import { useStrapiApi } from "~/uses/useMyFetch.js";
import qs from "qs";

export const useSurveyStore = defineStore("survey", () => {
  const currentSurveyId = ref(null);
  const steps = ref([]);
  const currentStepIndex = ref(0);
  const surveyAnswerId = ref(null);
  /*
  Формат опросника(survey)
  const survey = {
    surveyId: {
      parentId: "",
      answers: [
        {
          question: "",
          content: {
            options: [],
            customOption: "",
          },
        },
      ],
    },
  };
  */
  const survey = ref({});

  const fetchSurvey = async (id, parentId = null) =>
    await useStrapiApi(
      `/survey/${id}?${qs.stringify({
        populate: ["steps.options.survey"],
      })}`,
    ).then(({ data }) => {
      currentSurveyId.value = id;

      /*
        Создаём опрос если такого еще нет и записываем parentId
       */
      if (!survey.value[id]) {
        survey.value[id] = {
          parentId,
          answers: [],
        };
      }

      steps.value = data?.steps || [];

      const lastAnswerIndex = survey.value[id].answers.length - 1;
      currentStepIndex.value = lastAnswerIndex > 0 ? lastAnswerIndex : 0;
    });

  const postAnswer = async () =>
    await useStrapiApi("/survey/answer", {
      method: "POST",
      body: {
        data: {
          survey: currentSurveyId.value,
          content: answersToString(),
        },
      },
    }).then(({ data }) => {
      surveyAnswerId.value = data.documentId;
    });

  const updateAnswer = async (payload = {}) =>
    await useStrapiApi(`/survey/answer/update/${surveyAnswerId.value}`, {
      method: "POST",
      body: {
        data: {
          survey: currentSurveyId.value,
          content: answersToString(),
          ...payload,
        },
      },
    });

  const currentStep = computed(() => steps.value[currentStepIndex.value]);

  const isFirstStep = computed(() => {
    const surveysKeys = Object.keys(survey.value);

    /*
    Вычисление что это первый шаг в самом первом или единственном опросе
     */
    return (
      0 === surveysKeys.length ||
      (surveysKeys[0] === currentSurveyId.value && 0 === currentStepIndex.value)
    );
  });

  const isLastStep = computed(() => {
    return currentStepIndex.value === steps.value.length - 1;
  });

  /*
  Преобразование с форматированием всех ответов в строку для записи в базу
   */
  const answersToString = () => {
    return Object.values(survey.value)
      .flatMap(({ answers }) => {
        return answers.map(({ content, question }) => {
          const answerOptions = content.options.map(({ value }) => value);

          if (undefined !== content.customOption) {
            answerOptions.push(content.customOption);
          }

          return `${question}\n- ${answerOptions.join("\n- ")}`;
        });
      })
      .join("\n\n");
  };

  const addAnswer = async (answer, question) => {
    survey.value[currentSurveyId.value].answers[currentStepIndex.value] = {
      content: { ...answer },
      question,
    };

    if (null === surveyAnswerId.value) {
      return postAnswer();
    }

    return updateAnswer();
  };

  const toNextStep = async (answer) => {
    /*
     Добавляем ответ перед переходом
     */
    await addAnswer(answer, currentStep.value.question);

    /*
    Проверяем и достаем связаный опрос с текущим ответом.
    Работает в случае ради-кнопки или первый возможный вариант из чек-боксов
     */
    const linkedSurveyId = computed(() => {
      return answer.options.find(({ survey }) => survey !== null)?.survey
        .documentId;
    });

    if (linkedSurveyId.value !== undefined) {
      return fetchSurvey(linkedSurveyId.value, currentSurveyId.value);
    }

    if (!isLastStep.value) {
      currentStepIndex.value += 1;
    }
  };

  const toPrevStep = async () => {
    /*
       Определяем что есть предыдущий опрос и достаем его id
    */
    if (!isFirstStep.value && currentStepIndex.value === 0) {
      const parentId = survey.value[currentSurveyId.value].parentId;

      return fetchSurvey(parentId);
    }

    currentStepIndex.value -= 1;
  };

  return {
    steps,
    currentStepIndex,
    fetchSurvey,
    currentStep,
    isFirstStep,
    isLastStep,
    toNextStep,
    toPrevStep,
    currentSurveyId,
    surveyAnswerId,
    postAnswer,
    updateAnswer,
    survey,
  };
});
